/* eslint-disable solid/no-innerhtml */

import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from 'solid-js'
import { render } from 'solid-js/web'
import { Select } from '@kobalte/core'
import scrollIntoView from 'scroll-into-view-if-needed'
import { getSelectedOptions } from '../utilities'

const hiddenClass = 'u-hiddenVisually'

export default class {
  constructor(el, options) {
    const selectEl = el.querySelector('select')

    if (!selectEl) {
      throw new Error('No select element found')
    }

    options = Object.assign(
      {
        color: 'var(--color-white)',
      },
      options,
    )

    const selectLabel = document.body.querySelector(
      `label[for="${selectEl.getAttribute('id')}"]`,
    )

    if (selectLabel) {
      selectLabel.addEventListener('click', (e) => {
        e.preventDefault()
        el.querySelector('.js-selectTrigger').click()
      })
    }

    try {
      const optionEls = Array.from(selectEl.querySelectorAll('option'))

      const selectOptions = optionEls.map((option) => ({
        label: option.text,
        value: option.value,
        disabled: option.getAttribute('disabled') !== null,
      }))

      const isMultiple = selectEl.getAttribute('multiple') !== null
      const selectFieldset = selectEl.closest('fieldset')

      render(() => {
        const [selectedValues, setSelectedValues] = createSignal(
          getSelectedOptions(selectEl)
            .map((option) => ({
              label: option.text,
              value: option.value,
              disabled: option.getAttribute('disabled') !== null,
            }))
            .sort((a, b) =>
              options.order
                ? options.order.indexOf(a.value) -
                  options.order.indexOf(b.value)
                : 0,
            ),
        )

        function onChange(values) {
          setSelectedValues(Array.isArray(values) ? values : [values])
        }

        function sortOptionEls(a, b) {
          return (
            selectedValues().findIndex((value) => value.value === a.value) -
            selectedValues().findIndex((value) => value.value === b.value)
          )
        }

        createEffect(() => {
          /* Set options selected in hidden select */
          optionEls.forEach((option) => {
            if (
              selectedValues() &&
              selectedValues().find(
                (value) =>
                  value?.value && option?.value && value.value === option.value,
              )
            ) {
              if (option.getAttribute('selected') === null) {
                option.setAttribute('selected', 'selected')
              }
            } else {
              if (option.getAttribute('selected') !== null) {
                option.removeAttribute('selected')
              }
            }
          })

          /* Sort selected options in hidden select */
          optionEls.sort(sortOptionEls)

          /* Remove previous options to establish new order */
          selectEl.replaceChildren()

          selectEl.append(...optionEls)
        })

        function handleReset() {
          setSelectedValues([])
        }

        onMount(() => {
          selectEl.form.addEventListener('reset', handleReset)
        })

        onCleanup(() => {
          selectEl.form.removeEventListener('reset', handleReset)
        })

        function handleRemoveItem(value) {
          setSelectedValues((prev) =>
            prev.filter((prevValue) => prevValue.value !== value),
          )
        }

        return (
          <Select.Root
            classList={{
              Select: true,
              [`Select--${options.theme}`]: options.theme,
              [`Select--${options.size}`]: options.size,
            }}
            name={selectEl.getAttribute('name')}
            multiple={isMultiple}
            value={
              isMultiple
                ? selectedValues()
                : selectedValues()
                  ? selectedValues()[0]
                  : null
            }
            required={selectEl.getAttribute('required' !== null)}
            disabled={
              selectEl.getAttribute('disabled') !== null ||
              (selectFieldset &&
                selectFieldset.getAttribute('disabled') !== null)
            }
            readOnly={selectEl.getAttribute('readonly') !== null}
            options={selectOptions}
            optionValue="value"
            disallowEmptySelection={false}
            flip={false}
            fitViewport={true}
            overflowPadding={0}
            gutter={0}
            shift={0}
            onChange={onChange}
            optionTextValue="label"
            optionDisabled="disabled"
            placeholder={options?.placeholder}
            itemComponent={(props) => (
              <Select.Item
                class="Select-option"
                item={props.item}
                onFocus={(e) => {
                  window.requestAnimationFrame(() => {
                    scrollIntoView(e.target, {
                      scrollMode: 'if-needed',
                      block: 'nearest',
                      inline: 'nearest',
                    })
                  })
                }}
              >
                <Select.ItemLabel>{props.item.rawValue.label}</Select.ItemLabel>
                <Select.ItemIndicator>
                  <svg
                    class="Select-optionSymbol"
                    width="10"
                    height="10"
                    aria-hidden="true"
                  >
                    <use xlink:href="#symbol-cross" />
                  </svg>
                </Select.ItemIndicator>
              </Select.Item>
            )}
          >
            <Select.Trigger
              aria-label={options.placeholder}
              as="div"
              class="Select-trigger js-selectTrigger"
            >
              <Select.Value
                classList={{
                  'Select-value': true,
                  'has-value': selectedValues().length > 0,
                }}
              >
                {() => (
                  <div class="Select-valueContainer">
                    <Show when={isMultiple && selectedValues().length === 0}>
                      <span class="Select-valuePlaceholder">
                        {options.placeholder}
                      </span>
                    </Show>
                    <For each={selectedValues()}>
                      {(option) => (
                        <SelectedItem {...option} onRemove={handleRemoveItem} />
                      )}
                    </For>
                  </div>
                )}
              </Select.Value>
              <svg
                class="Select-triggerSymbol"
                width="16"
                height="10"
                aria-hidden="true"
              >
                <use xlink:href="#symbol-chevron-down" />
              </svg>
            </Select.Trigger>
            <Select.Portal class="Select-portal">
              <Select.Content
                classList={{
                  'Select-content': true,
                  [`Select-content--${options.theme}`]: options.theme,
                  'Select-content--sm': selectOptions.length < 2,
                }}
                style={{
                  '--Select-color': options.color,
                }}
              >
                <Select.Listbox class="Select-list" />
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        )
      }, el)
    } catch (error) {
      selectEl.classList.remove(hiddenClass)

      selectEl.style.cssText = `width: 100%;
      background: rgba(0,0,0,0.03);
      padding: 1em;`

      selectEl.addEventListener('change', () => {
        Array.from(selectEl.options).forEach((optionEl) => {
          const isSelected = Array.from(selectEl.selectedOptions).includes(
            optionEl,
          )

          if (isSelected) {
            optionEl.setAttribute('selected', 'selected')
          } else {
            optionEl.removeAttribute('selected')
          }
        })
      })
    }
  }
}

const SelectedItem = (props) => (
  <span class="Select-item Pill" onPointerDown={(e) => e.stopPropagation()}>
    <span class="Select-itemText Pill-text u-textTruncate">{props.label}</span>
    <button
      class="Select-itemRemove Pill-button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={() => props.onRemove(props.value)}
      type="button"
    >
      <span class="u-hiddenVisually">Ta bort</span>
      <svg
        class="Select-itemRemoveSymbol Pill-buttonSymbol"
        width="10"
        height="10"
        aria-hidden="true"
      >
        <use xlink:href="#symbol-cross" />
      </svg>
    </button>
  </span>
)
